/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/indent */
import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { MatSelectChange } from "@angular/material/select";
import { Field, QueryBuilderConfig, Rule } from "angular2-query-builder";
import { isEmpty } from "lodash";
import * as moment from "moment";
import * as momentTz from "moment-timezone";
import { Observable } from "rxjs";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { DataTable, DataTableColumn, DataTableParams } from "../../../shared/components/data-table/data-table.type";
import { NotificationService } from "../../../shared/components/notification/notification.service";
import { TableColumn } from "../../../shared/components/transfer/transfer-table/transfer-table.type";
import { TransferData } from "../../../shared/components/transfer/transfer.type";
import { yesNoOptions } from "../../../shared/constants/components";
import { FORMAT } from "../../../shared/constants/formats";
import { pattern } from "../../../shared/constants/patterns";
import { MomentPipe } from "../../../shared/pipes/moment/moment.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { getMapOf } from "../../../shared/utils/collections/map";
import { getAssetCategories, getAssetCoverageStatuses } from "../assets/asset";
import { Asset, AssetModel, AssetOem, AssetType } from "../assets/asset.type";
import { Company } from "../companies/company.type";
import { contractStatuses, contractTypes } from "../contracts/contract";
import { Contract } from "../contracts/contract.type";
import { Country } from "../locations/location.type";
import { Partner, PartnerSpecialTypes } from "../partners/partner.type";
import { ticketStatuses } from "../tickets/ticket";
import { Ticket } from "../tickets/ticket.type";
import { ReportingService } from "./reporting.service";
import {
  FieldExt,
  FieldMap,
  FieldOrFilter,
  FieldType,
  Operator,
  QueryBuilderConfigExt,
  ReportingData,
  ReportingEntities,
  ReportingEntity,
  ReportingTransferItem,
  RuleSet,
  RuleWithAuxValue,
} from "./reporting.type";

/** default data for fields */
const allowedDates = {
  min: "2000-01-01T00:00:00.000Z",
  max: "2050-12-31T23:59:59.999Z",
};

// #region Validators
const isValidText = (rule: Rule) =>
  rule?.value && pattern.words.test(rule.value) ? null : { text: { rule, message: "Not valid" } };

const isValidDate = (rule: Rule) => {
  let date = rule.value ? moment(rule.value).utc() : rule.value;

  if (date && rule.field === "endDate") {
    date = moment(date).endOf("day");
  }

  if (date && moment(date).utc().isBetween(allowedDates.min, allowedDates.max, null, "[]")) {
    return null;
  }

  return { date: { rule, message: "Not valid" } };
};

const isValidBetweenDate = (rule: RuleWithAuxValue) => {
  let date = rule.value ? moment(rule.value).utc() : rule.value;
  let auxDate = rule.auxValue ? moment(rule.auxValue).utc() : rule.auxValue;

  if (date && rule.field === "endDate") {
    date = moment(date).endOf("day");
  }

  if (auxDate && rule.field === "endDate") {
    auxDate = moment(auxDate).endOf("day");
  }

  if (
    date &&
    moment(date).utc().isBetween(allowedDates.min, allowedDates.max, null, "[]") &&
    auxDate &&
    moment(auxDate).utc().isBetween(allowedDates.min, allowedDates.max, null, "[]") &&
    moment(date).utc().isSameOrBefore(moment(auxDate).utc())
  ) {
    return null;
  }

  return { date: { rule, message: "Not valid" } };
};

const isValidPrice = (rule: Rule) =>
  rule?.value && pattern.price.test(rule.value) ? null : { price: { rule, message: "Not valid" } };

// #endregion

// #region Operators

const operatorList: Operator[] = [
  { id: "$eq", title: "Equal", type: ["string", "number", "category", "date"] },
  { id: "$ne", title: "Not Equal", type: ["string", "number", "category", "date"] },
  { id: "$cont", title: "Contains", type: ["string"] },
  { id: "$ncont", title: "Not Contains", type: ["string"] },
  { id: "$in", title: "In", type: ["string"], hidden: true },
  { id: "$nin", title: "Not In", type: ["string"], hidden: true },
  { id: "$between", title: "Between", type: ["date", "dateTime"] },
  { id: "$starts", title: "Starts With", type: ["string", "number"] },
  { id: "$ends", title: "Ends With", type: ["string", "number"] },
  { id: "$gt", title: "Greater Than", type: ["number", "date", "dateTime"] },
  { id: "$gte", title: "Greater Than or Equal", type: ["number", "date", "dateTime"] },
  { id: "$lt", title: "Less Than", type: ["number", "date", "dateTime"] },
  { id: "$lte", title: "Less Than or Equal", type: ["number", "date", "dateTime"] },
];

const getOperatorsByType = (type: FieldType) =>
  operatorList.filter((op) => !op.hidden && op.type.includes(type)).map((op) => op.id);

// #endregion

@Component({
  selector: "app-reporting",
  templateUrl: "./reporting.component.html",
  styleUrls: ["./reporting.component.scss"],
})
export class ReportingComponent implements OnInit {
  asset: ReportingEntity<
    Asset & {
      partner: Partner;
      country: Country;
      state: string;
      locationName: string;
      address: string;
      address2: string;
      address3: string;
      zip: string;
      city: string;
      oem: AssetOem;
      model: AssetModel;
    }
  > = {
    id: "asset",
    title: "Asset",
    fields: {
      name: {
        name: "Name",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
      },
      serialNumber: {
        name: "Serial #",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
      },
      category: {
        name: "Category",
        type: "category",
        operators: getOperatorsByType("category"),
        options: getAssetCategories().map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: getAssetCategories()[0].value,
        useOnlyAs: "field",
      },
      coverageStatus: {
        name: "Coverage Status",
        type: "category",
        operators: getOperatorsByType("category"),
        options: getAssetCoverageStatuses().map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: getAssetCoverageStatuses()[0].value,
      },
      startDate: {
        name: "Start Date",
        type: "date",
        operators: getOperatorsByType("date"),
        validator: (rule) => (rule.operator === "$between" ? isValidBetweenDate(rule) : isValidDate(rule)),
        column: {
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
      },
      endDate: {
        name: "End Date",
        type: "date",
        operators: getOperatorsByType("date"),
        validator: (rule) => (rule.operator === "$between" ? isValidBetweenDate(rule) : isValidDate(rule)),
        column: {
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
      },
      sla: {
        name: "SLA",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { sla: { value: true } },
        validator: (rule) => isValidText(rule),
        csvMapperKey: "sla.value",
        extraSelectKeys: { sla: { value: true } },
        column: {
          render: (row) => row.sla?.value,
        },
      },
      oem: {
        name: "OEM",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        relationField: { type: { model: { oem: { name: true } } } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.type.model.oem.name,
        },
      },
      model: {
        name: "Model #",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        relationField: { type: { model: { name: true } } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.type.model.name,
        },
      },
      type: {
        name: "Type",
        type: "string",
        operators: getOperatorsByType("string"),
        validator: (rule) => isValidText(rule),
        relationField: { type: { name: true } },
        column: {
          render: (row) => row.type.name,
        },
      },
      dmr: {
        name: "DMR",
        type: "category",
        operators: getOperatorsByType("category"),
        options: yesNoOptions.map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: yesNoOptions[0].value,
        column: {
          render: (row) => yesNoOptions.find((opt) => opt.value === row.dmr)?.label,
        },
      },
      monitoring: {
        name: "Monitoring",
        type: "category",
        operators: getOperatorsByType("category"),
        options: yesNoOptions.map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: yesNoOptions[0].value,
        column: {
          render: (row) => yesNoOptions.find((opt) => opt.value === row.monitoring)?.label,
        },
      },
      contract: {
        name: "Contract #",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { contract: { number: true } },
        csvMapperKey: "contract.numberVersion",
        extraSelectKeys: { contract: { version: true }, contractId: true },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.contract?.number && `${row.contract?.number}-${row.contract?.version}`,
        },
      },
      internalProvider: {
        name: "Internal Provider",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { internalProvider: { name: true } },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
        column: {
          render: (row) => row.internalProvider?.name,
        },
      },
      channelWorksCost: {
        name: "CW Cost",
        type: "string",
        operators: getOperatorsByType("number"),
        useOnlyAs: "field",
        validator: (rule) => isValidPrice(rule),
        isOutOfScope: (user) => !user.isGlobal(),
        column: {
          render: (row) =>
            row.channelWorksCost &&
            (+row.channelWorksCost).toLocaleString("en-US", { style: "currency", currency: "USD" }),
        },
      },
      partnerPrice: {
        name: "Partner Price",
        type: "string",
        operators: getOperatorsByType("number"),
        useOnlyAs: "field",
        validator: (rule) => isValidPrice(rule),
        isOutOfScope: (user) => user.isCompanyOrViewAsCompany(),
        column: {
          render: (row) =>
            row.partnerPrice && (+row.partnerPrice).toLocaleString("en-US", { style: "currency", currency: "USD" }),
        },
      },
      buyPrice: {
        name: "Buy Price",
        type: "string",
        operators: getOperatorsByType("number"),
        useOnlyAs: "field",
        validator: (rule) => isValidPrice(rule),
        isOutOfScope: (user) => !(user.isGlobal() || (!user.isGlobal() && this.isRelutech(user))),
        column: {
          render: (row) =>
            row.buyPrice && (+row.buyPrice).toLocaleString("en-US", { style: "currency", currency: "USD" }),
        },
      },
      description: {
        name: "Description",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
      },
      company: {
        name: "Company",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { company: { name: true } },
        extraSelectKeys: { companyId: true },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
        column: {
          render: (row) => row.company?.name,
        },
      },
      partner: {
        name: "Partner",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { company: { partner: { name: true } } },
        extraSelectKeys: { company: { id: true } },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
        column: {
          render: (row) => row.company?.partner?.name,
        },
      },
      locationName: {
        name: "Location Name",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        relationField: { location: { name: true } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.name,
        },
      },
      address: {
        name: "Address 1",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        relationField: { location: { address1: true } },
        extraSelectKeys: {
          location: { address1: true },
        },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.address1,
        },
      },
      address2: {
        name: "Address 2",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        relationField: { location: { address2: true } },
        extraSelectKeys: {
          location: { address2: true },
        },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.address2,
        },
      },
      address3: {
        name: "Address 3",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        relationField: { location: { address3: true } },
        extraSelectKeys: {
          location: { address3: true },
        },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.address3,
        },
      },
      country: {
        name: "Country",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { location: { country: { name: true } } },
        extraSelectKeys: { location: { id: true } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.country?.name,
        },
      },
      city: {
        name: "City",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { location: { city: true } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.city,
        },
      },
      state: {
        name: "State",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { location: { state: true } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.state,
        },
      },
      zip: {
        name: "Zip",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { location: { zip: true } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.location?.zip,
        },
      },
      hasConfigurationNotes: {
        name: "Configuration Notes",
        type: "category",
        operators: getOperatorsByType("category"),
        options: yesNoOptions.map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: yesNoOptions[0].value,
        column: {
          render: (row) => yesNoOptions.find((opt) => opt.value === row.hasConfigurationNotes)?.label,
        },
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
      },
    },
  };

  contract: ReportingEntity<Contract & { partner: Partner }> = {
    id: "contract",
    title: "Contract",
    fields: {
      name: {
        name: "Name",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
      },
      startDate: {
        name: "Start Date",
        type: "date",
        operators: getOperatorsByType("date"),
        validator: (rule) => (rule.operator === "$between" ? isValidBetweenDate(rule) : isValidDate(rule)),
        column: {
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
      },
      endDate: {
        name: "End Date",
        type: "date",
        operators: getOperatorsByType("date"),
        validator: (rule) => (rule.operator === "$between" ? isValidBetweenDate(rule) : isValidDate(rule)),
        column: {
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
      },
      type: {
        name: "Type",
        type: "category",
        operators: getOperatorsByType("category"),
        options: contractTypes.map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: contractTypes[0].value,
        useOnlyAs: "field",
      },
      number: {
        name: "Contract #",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        csvMapperKey: "numberVersion",
        extraSelectKeys: { version: true },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isCompanyOrViewAsCompany(),
        column: {
          render: (row) => row.numberVersion,
        },
      },
      sfOpportunity: {
        name: "SF Opportunity",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
      },
      maintenanceProvider: {
        name: "Maintenance Provider",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { maintenanceProvider: { name: true } },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
        column: {
          render: (row) => row.maintenanceProvider?.name,
        },
      },
      status: {
        name: "Status",
        type: "category",
        operators: getOperatorsByType("category"),
        options: contractStatuses.map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: contractStatuses[0].value,
      },
      company: {
        name: "Company",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { company: { name: true } },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
        column: {
          render: (row) => row.company?.name,
        },
      },
      partner: {
        name: "Partner",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { company: { partner: { name: true } } },
        extraSelectKeys: { company: { id: true } },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
        column: {
          render: (row) => row.company?.partner?.name,
        },
      },
      notes: {
        name: "Internal Notes",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
      },
    },
  };

  ticket: ReportingEntity<
    Ticket & { company: Company; partner: Partner; requesterEmail: string; country: Country; state: string }
  > = {
    id: "ticket",
    title: "Ticket",
    fields: {
      zendeskId: {
        name: "Zendesk ID",
        type: "string",
        operators: getOperatorsByType("number"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
      },
      company: {
        name: "Company",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { asset: { company: { name: true } } },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
        column: {
          render: (row) => row.asset?.company?.name,
        },
      },
      partner: {
        name: "Partner",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { asset: { company: { partner: { name: true } } } },
        extraSelectKeys: { asset: { company: { id: true } } },
        validator: (rule) => isValidText(rule),
        isOutOfScope: (user) => user.isPartnerOrViewAsPartner() || user.isCompanyOrViewAsCompany(),
        column: {
          render: (row) => row.asset?.company?.partner?.name,
        },
      },
      asset: {
        name: "Serial #",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        relationField: { asset: { serialNumber: true } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.asset?.serialNumber,
        },
      },
      openedAt: {
        name: "Open Date",
        type: "date",
        operators: getOperatorsByType("dateTime"),
        useOnlyAs: "field",
        validator: (rule) => (rule.operator === "$between" ? isValidBetweenDate(rule) : isValidDate(rule)),
        column: {
          render: (row) => momentTz(row.openedAt).tz("America/New_York").format(FORMAT.DATE_TIME),
        },
      },
      solvedAt: {
        name: "Solved Date",
        type: "date",
        operators: getOperatorsByType("dateTime"),
        validator: (rule) => (rule.operator === "$between" ? isValidBetweenDate(rule) : isValidDate(rule)),
        column: {
          render: (row) => (row.solvedAt ? momentTz(row.solvedAt).tz("America/New_York").format(FORMAT.DATE_TIME) : ""),
        },
      },
      status: {
        name: "Status",
        type: "category",
        operators: getOperatorsByType("category"),
        options: ticketStatuses.map((opt) => ({ value: opt.value, name: opt.label })),
        defaultValue: ticketStatuses[0].value,
      },
      requesterEmail: {
        name: "Requester",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        extraSelectKeys: { requesterEmail: true },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.requester?.email,
        },
      },
      subject: {
        name: "Subject",
        type: "string",
        operators: getOperatorsByType("string"),
        useOnlyAs: "field",
        validator: (rule) => isValidText(rule),
      },
      country: {
        name: "Country",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { asset: { location: { country: { name: true } } } },
        extraSelectKeys: { asset: { id: true, location: { id: true, country: { id: true } } } }, // "id: true" is required in both cases in order to ensure that the field is populated
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.asset?.location?.country?.name,
        },
      },
      state: {
        name: "State",
        type: "string",
        operators: getOperatorsByType("string"),
        relationField: { asset: { location: { state: true } } },
        validator: (rule) => isValidText(rule),
        column: {
          render: (row) => row.asset?.location?.state,
        },
      },
    },
  };

  // Common data
  currentUser: UserInfo;

  // Component data
  operatorList: Operator[] = operatorList;
  defaultConfig: ReportingEntity<ReportingEntities>[] = [this.asset, this.contract, this.ticket] as any;

  currentConfig: ReportingEntity<ReportingEntities>;
  // Transfer (columns) data
  transferData: TransferData<ReportingTransferItem>;
  // Query Builder (filters) data
  queryBuilderForm: FormControl;
  queryBuilderConfig: QueryBuilderConfig;
  queryBuilderData: RuleSet<ReportingEntities>;
  #queryBuilderEnabled: boolean;
  // Table data
  defaultTableData: DataTable<ReportingEntities>;
  currentTableData: DataTable<ReportingEntities>;
  canGenerateReport: boolean;
  isReportGenerated: boolean;

  get queryBuilderEnabled(): boolean {
    return this.#queryBuilderEnabled;
  }
  set queryBuilderEnabled(value: boolean) {
    this.#queryBuilderEnabled = value;
    if (!value) {
      this.setDefaultQueryBuilderData();
    }
  }
  constructor(
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private reportingService: ReportingService,
    private formBuilder: FormBuilder,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  // #region Life cycle methods

  ngOnInit(): void {
    this.setDefaultTransferData();
    this.setDefaultQueryBuilderData();
    this.setDefaultTableData();

    this.queryBuilderForm = this.formBuilder.control(this.queryBuilderData);
  }

  // #endregion

  getReport(
    onResult: (data: PaginatedResponse<ReportingEntities>) => void,
    onError: (error: string) => void,
    pagination: Pick<DataTableParams, "pagination">,
  ): void {
    const handleError = (err: Error): void => {
      this.notificationService.error(err?.message || "Unable to generate the report");
      this.isReportGenerated = false;
    };

    const reportingData = this.getReportingData(false);

    try {
      (
        this.reportingService.getData(reportingData, pagination) as Observable<PaginatedResponse<ReportingEntities>>
      ).subscribe(
        (data) => {
          onResult(data);
        },
        (error) => {
          onError(error);
          handleError(error);
        },
      );
    } catch (error) {
      console.error(error);
      handleError(error);
    }
  }

  // #region Handlers

  /** Called when selected entity changes */
  onEntityChange(event: MatSelectChange): void {
    const { value: entity } = event;
    this.updateConfig({ entity });
  }

  /** Called when selected fields changes */
  onFieldsChange(selectedFields: ReportingTransferItem[]): void {
    this.updateConfig({ selectedFields });
  }

  /** Called when the query builder checkbox changes */
  onCheckBoxChange(event: MatCheckboxChange): void {
    this.queryBuilderEnabled = event.checked;
    this.validateCanGenerateReport();
  }

  /** Called when any field component inside query builder changes */
  onQueryBuilderFieldChange(event: Event, rule: RuleWithAuxValue, field: Field): void {
    this.validateCanGenerateReport();
    // Here we get the element reference to be used on the field validation.
    // The event can be a html event or a component event like MatDatepickerInputEvent or MatSelectChange
    // that is why we have to infer the target
    let targetElement = null;
    if (event instanceof MatSelectChange) {
      targetElement = event.source.trigger.nativeElement;
    } else if (event instanceof MatDatepickerInputEvent) {
      targetElement = event.targetElement;
    } else {
      targetElement = event.target;
    }
    const element = targetElement.closest("li");
    this.convertEndDateField(rule);
    this.validateField(element, rule, field);
  }

  /** Called when any component inside query builder changes */
  onQueryBuilderChange(): void {
    this.validateCanGenerateReport();
  }

  /** Called when the "generate" button is clicked */
  onGenerateReport(): void {
    this.validateCanGenerateReport();

    this.queryBuilderForm.updateValueAndValidity();

    if (isEmpty(this.queryBuilderData?.rules) && this.queryBuilderEnabled) {
      this.notificationService.error(
        "Filters are enabled but none is set. Please add one or more filters, or disable the filters and try again",
      );

      return;
    }

    if (this.queryBuilderForm.invalid) {
      this.queryBuilderForm.markAllAsTouched();
      this.notificationService.error(
        "One or more filters are invalid. Please correct or remove these filters and try again",
      );

      return;
    }

    this.isReportGenerated = true;
    this.canGenerateReport = false;
  }

  // #endregion

  // #region Private Methods

  /** Get the data required to generate a report */
  private getReportingData(exportAsCsv: boolean): ReportingData {
    const entity = this.currentConfig.id;
    const fields = this.currentTableData.columns.map((col) => col.prop) as string[];

    const data: ReportingData<ReportingEntities> = {
      entity,
      fields,
      query: this.queryBuilderData,
      config: this.currentConfig,
      exportAsCsv,
    };

    return data;
  }

  /** Updates the report configuration (selected fields and query builder/filters) */
  private updateConfig(data: { entity?: string; selectedFields?: ReportingTransferItem[] }): void {
    this.validateCanGenerateReport();

    const { entity, selectedFields } = data || {};

    if (entity) {
      this.currentConfig = this.getConfigByEntity(entity);
      const dataSource = this.getTransferItemsByConfig(this.currentConfig);
      this.setDefaultTransferData();
      this.updateTransferData({ ...this.transferData, dataSource });
      const fields = this.getAvailableFieldForQueryBuilder(this.currentConfig.fields);
      this.setDefaultQueryBuilderData();
      this.updateQueryBuilderConfig({ ...this.queryBuilderConfig, fields });
    }

    this.updateReport(selectedFields || []);
  }

  /** Updates the report data */
  private updateReport(selectedFields: ReportingTransferItem[]): void {
    const entity = this.currentConfig.id;

    this.currentTableData = {
      ...this.defaultTableData,
      ...this.currentTableData,
      columns: selectedFields.map<DataTableColumn<ReportingEntities>>((f) => ({
        prop: f.id,
        name: f.fieldName,
        draggable: false,
        sortable: false,
        width: 150,
        ...this[entity].fields[f.id].column,
      })) as DataTableColumn<ReportingEntities>[],
    };

    // If "currentTableData" exist then we can set the "toolBar.exportCsv" property
    if (this.currentTableData) {
      /** get the required data to generate the report */
      const data = this.currentTableData && this.getReportingData(true);

      this.currentTableData = {
        ...this.currentTableData,
        toolBar: {
          ...this.currentTableData.toolBar,
          exportCsv: {
            output: "email",
            data: {
              email: () => this.reportingService.getData(data) as Observable<void>,
            },
          },
        },
      };
    }
  }

  /** Set default transfer data (columns and empty data) */
  private setDefaultTransferData(): void {
    const columns: TableColumn<ReportingTransferItem>[] = [
      {
        id: "_select_",
        type: "checkbox",
        checked: false,
      },
      {
        id: "fieldName",
        title: "Field",
      },
      {
        id: "_empty_",
        sort: { disabled: true },
      },
    ] as TableColumn<ReportingTransferItem>[];

    this.transferData = { columns, dataSource: [] };
  }

  /** Set default query builder data */
  private setDefaultQueryBuilderData(): void {
    this.queryBuilderData = {
      condition: "and",
      rules: [],
    };
  }

  /** Set default table data */
  private setDefaultTableData(): void {
    this.defaultTableData = {
      id: "report",
      entityName: null,
      selection: { hidden: true },
      columns: [],
      toolBar: {
        exportCsv: {
          hidden: true,
        },
        refreshTable: {
          hidden: false,
        },
        toggleColumns: {
          hidden: true,
        },
      },
    };
  }

  /** Get reporting configuration by entity id */
  private getConfigByEntity(entity: string): ReportingEntity<ReportingEntities> {
    return this.defaultConfig.find((config) => config.id === entity);
  }

  /** Get the items (or fields) for the transfer component by a config.
   *
   * It maps each available field as a transfer item.
   * Fields are previously filtered by properties or functions like `useOnlyAs`or `isOutOfScope`
   */
  private getTransferItemsByConfig(config: ReportingEntity<ReportingEntities>): ReportingTransferItem[] {
    // Here we convert the objects of fields in array of fields for a better handle
    const fields = Object.keys(config.fields).map((key: keyof ReportingEntities) => ({
      id: key,
      ...config.fields[key],
    }));
    // Here we filter the field that are not allowed to be displayed as field
    const availableFields = fields.filter((field) => this.filterToApply(field, "field"));
    return availableFields.map((field) => ({
      id: field.id,
      fieldName: field.name,
      selected: false,
    })) as ReportingTransferItem[];
  }

  /** Updates transfer data object */
  private updateTransferData(transferData: TransferData<ReportingTransferItem>): void {
    this.transferData = { ...this.transferData, ...transferData };
  }

  /** Get the available fields for the query builder (filters) component.
   * Fields are filtered by properties or functions like `useOnlyAs`or `isOutOfScope`
   */
  private getAvailableFieldForQueryBuilder(fieldsMap: FieldMap<ReportingEntities>): FieldMap<ReportingEntities> {
    // Here we convert the mapping of fields in array of fields for a better handle
    const fields = Object.keys(fieldsMap).map((key: keyof ReportingEntities) => ({
      id: key,
      ...fieldsMap[key],
    }));
    // Here we filter the field that are not allowed to be displayed as field
    const availableFields = fields.filter((field) => this.filterToApply(field, "filter"));
    const availableFieldsMap = getMapOf(availableFields, "id", { removeSelectedKey: true });
    return Object.fromEntries(availableFieldsMap);
  }

  /** Updates query builder config (filters) */
  private updateQueryBuilderConfig(config: QueryBuilderConfigExt<ReportingEntities>): void {
    this.queryBuilderConfig = { ...config };
  }

  /** Filter condition to apply in order to hide a field */
  private filterToApply(field: FieldExt<ReportingEntities>, fieldOrFilter: FieldOrFilter): boolean {
    return (!field.useOnlyAs || field.useOnlyAs === fieldOrFilter) && !field.isOutOfScope?.(this.currentUser);
  }

  /** Fix the time of endDate field to 23:59:59.999 */
  private convertEndDateField(rule: RuleWithAuxValue): void {
    if (rule.field === "endDate") {
      if (rule.value) {
        rule.value = moment(rule.value).utc().endOf("day");
      }
      if (rule.operator === "$between" && rule.auxValue) {
        rule.auxValue = moment(rule.auxValue).utc().endOf("day");
      }
    }
  }

  /** Validates each rule/field and marks it as an error if it is invalid */
  private validateField(element: HTMLElement, rule: RuleWithAuxValue, field: FieldExt<unknown>): void {
    this.queryBuilderForm.updateValueAndValidity();

    if (this.queryBuilderForm.invalid && field?.validator(rule, null)) {
      element.style.borderColor = "#ff4081";
    } else {
      element.style.borderColor = "#ccc";
    }
  }

  /** Validate if can generate the report */
  private validateCanGenerateReport(): void {
    if (isEmpty(this.queryBuilderData?.rules) && this.queryBuilderEnabled) {
      this.canGenerateReport = false;
    } else {
      this.canGenerateReport = true;
      this.isReportGenerated = false;
    }
  }

  private isRelutech(user: UserInfo): boolean {
    return (
      user.partner?.specialType === PartnerSpecialTypes.Relutech ||
      user.company?.partner?.specialType === PartnerSpecialTypes.Relutech
    );
  }

  // #endregion
}
